/**
 * 宫格拼接核心类，与原实现保持一致
 */
export default class GridStitcher {
  constructor() {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
  }

  calculateLayout(options) {
    const { rows, cols, totalWidth, totalHeight, spacing } = options;
    const availableWidth = totalWidth - spacing * (cols - 1);
    const availableHeight = totalHeight - spacing * (rows - 1);
    const cellWidth = Math.floor(availableWidth / cols);
    const cellHeight = Math.floor(availableHeight / rows);

    const cells = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = col * (cellWidth + spacing);
        const y = row * (cellHeight + spacing);
        cells.push({
          row,
          col,
          x: Math.round(x),
          y: Math.round(y),
          width: cellWidth,
          height: cellHeight
        });
      }
    }

    return { cells, cellWidth, cellHeight, totalWidth, totalHeight };
  }

  calculateHeterogeneousLayout(options) {
    const { areas, totalWidth, totalHeight, spacing } = options;
    let totalGridWidth = 0;
    let totalGridHeight = 0;

    areas.forEach((area) => {
      totalGridWidth = Math.max(totalGridWidth, area.x + area.w);
      totalGridHeight = Math.max(totalGridHeight, area.y + area.h);
    });

    const gridUnitWidth =
      (totalWidth - spacing * (totalGridWidth - 1)) / totalGridWidth;
    const gridUnitHeight =
      (totalHeight - spacing * (totalGridHeight - 1)) / totalGridHeight;

    const cells = areas.map((area, index) => {
      const x = area.x * gridUnitWidth + spacing * area.x;
      const y = area.y * gridUnitHeight + spacing * area.y;
      const width = area.w * gridUnitWidth;
      const height = area.h * gridUnitHeight;

      return {
        index,
        x: Math.round(x),
        y: Math.round(y),
        width: Math.round(width),
        height: Math.round(height)
      };
    });

    return { cells, totalWidth, totalHeight };
  }

  drawImageToCell(image, cell, fitMode = 'cover') {
    const { x, y, width, height } = cell;
    const ctx = this.ctx;

    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, width, height);
    ctx.clip();

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x, y, width, height);

    const imageRatio = image.width / image.height;
    const cellRatio = width / height;

    let drawWidth, drawHeight, drawX, drawY;

    if (fitMode === 'cover') {
      if (imageRatio > cellRatio) {
        drawHeight = height;
        drawWidth = image.width * (height / image.height);
        drawX = x - (drawWidth - width) / 2;
        drawY = y;
      } else {
        drawWidth = width;
        drawHeight = image.height * (width / image.width);
        drawX = x;
        drawY = y - (drawHeight - height) / 2;
      }
    } else {
      if (imageRatio > cellRatio) {
        drawWidth = width;
        drawHeight = image.height * (width / image.width);
        drawX = x;
        drawY = y + (height - drawHeight) / 2;
      } else {
        drawHeight = height;
        drawWidth = image.width * (height / image.height);
        drawX = x + (width - drawWidth) / 2;
        drawY = y;
      }
    }

    ctx.drawImage(
      image,
      0,
      0,
      image.width,
      image.height,
      Math.round(drawX),
      Math.round(drawY),
      Math.round(drawWidth),
      Math.round(drawHeight)
    );

    ctx.restore();
  }

  drawGridLines(cells) {
    const ctx = this.ctx;
    ctx.strokeStyle = '#f0f0f0';
    ctx.lineWidth = 1;
    cells.forEach((cell) => {
      ctx.strokeRect(cell.x, cell.y, cell.width, cell.height);
    });
  }

  stitchImages(options) {
    return new Promise((resolve, reject) => {
      const {
        images,
        rows = 2,
        cols = 2,
        areas,
        totalSize = 800,
        spacing = 2,
        fitMode = 'cover',
        format = 'png',
        quality = 0.95
      } = options;

      if (!images || images.length === 0) {
        reject(new Error('没有图片可拼接'));
        return;
      }

      const imageObjects = [];
      const loadPromises = images.map((imgSrc) => {
        return new Promise((imgResolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            imageObjects.push(img);
            imgResolve();
          };
          img.onerror = () => imgResolve();
          img.src = imgSrc;
        });
      });

      Promise.all(loadPromises)
        .then(() => {
          const validImages = imageObjects.filter((img) => img.complete);
          if (validImages.length === 0) {
            reject(new Error('所有图片加载失败'));
            return;
          }

          let layout;
          const isHeterogeneous = !!areas;

          this.canvas.width = totalSize;
          this.canvas.height = totalSize;

          this.ctx.fillStyle = '#f9f9f9';
          this.ctx.fillRect(0, 0, totalSize, totalSize);

          if (isHeterogeneous) {
            layout = this.calculateHeterogeneousLayout({
              areas,
              totalWidth: totalSize,
              totalHeight: totalSize,
              spacing
            });
          } else {
            layout = this.calculateLayout({
              rows,
              cols,
              totalWidth: totalSize,
              totalHeight: totalSize,
              spacing
            });
          }

          const cellsToUse = layout.cells.slice(0, validImages.length);
          cellsToUse.forEach((cell, index) => {
            this.drawImageToCell(validImages[index], cell, fitMode);
          });

          if (spacing > 0) {
            this.drawGridLines(cellsToUse);
          }

          let dataUrl;
          if (format === 'jpeg' || format === 'webp') {
            dataUrl = this.canvas.toDataURL(`image/${format}`, quality);
          } else {
            dataUrl = this.canvas.toDataURL(`image/${format}`);
          }

          resolve(dataUrl);
        })
        .catch(reject);
    });
  }
}